import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/design-system';

export function AdminSekretlyMethodology() {
  const [methodology, setMethodology] = useState<string | null>(null);
  const [synthesisNote, setSynthesisNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const { data, error: fnError } = await supabase.functions.invoke('sekretly-methodology');
      if (fnError || !data) {
        setError("Couldn't load the methodology text. Please try again.");
      } else {
        setMethodology(data.methodology ?? null);
        setSynthesisNote(data.synthesisNote ?? null);
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="min-h-dvh bg-app-bg flex items-center justify-center"><Spinner /></div>;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-3xl">
        <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Sekretly Methodology</h1>
        <p className="text-app-muted text-sm mb-8">
          The Sekretly Methodology for Understanding People, as distilled into live instructions.
          This applies to every AI persona (Admin → AI Personas) — not just Alma — since whichever
          persona is active for a conversation follows the same method. This is read-only — it's the
          exact text running in production, pulled directly from{' '}
          <code className="text-xs bg-app-card border border-app-border rounded px-1.5 py-0.5">
            supabase/functions/_shared/sekretlyMethodology.ts
          </code>, shared by every conversation surface (sandbox and live).
        </p>

        {error && (
          <div className="rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 mb-6 text-sm text-crimson">
            {error}
          </div>
        )}

        {methodology && (
          <section className="bg-app-card border border-app-border rounded-2xl p-5 mb-6">
            <h2 className="text-sm font-semibold text-app-fg mb-1">How the AI asks questions</h2>
            <p className="text-xs text-app-muted mb-4">Used while the conversation is happening — this is what shapes every question the active persona asks.</p>
            <pre className="whitespace-pre-wrap font-sans text-sm text-app-fg-2 leading-relaxed">{methodology}</pre>
          </section>
        )}

        {synthesisNote && (
          <section className="bg-app-card border border-app-border rounded-2xl p-5">
            <h2 className="text-sm font-semibold text-app-fg mb-1">How the AI summarizes it</h2>
            <p className="text-xs text-app-muted mb-4">Used when building the traveler's taste portrait from the conversation.</p>
            <pre className="whitespace-pre-wrap font-sans text-sm text-app-fg-2 leading-relaxed">{synthesisNote}</pre>
          </section>
        )}
      </div>
    </div>
  );
}

export default AdminSekretlyMethodology;
